import React, { useState } from 'react';
import { Download, Share2, Plus, Check, MonitorDown, Smartphone } from 'lucide-react';
import usePwaInstall from '../hooks/usePwaInstall';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';

/**
 * InstallAppButton — an always-visible "Install App" CTA that works across
 * platforms:
 *   • Chromium / Edge / Android: fires the native beforeinstallprompt dialog.
 *   • iOS Safari: opens step-by-step "Share → Add to Home Screen" instructions.
 *   • Desktop Safari / Firefox (no prompt): opens generic browser-menu steps.
 * Renders nothing when the app is already installed / running standalone.
 *
 * `className` styles the trigger button so it can match any surface.
 */
const InstallAppButton = ({ className = '', label = 'Install App', testId = 'install-app-btn' }) => {
  const { canPrompt, isIOS, isStandalone, installed, promptInstall } = usePwaInstall();
  const [open, setOpen] = useState(false);

  if (isStandalone || installed) return null;

  const handleClick = async () => {
    if (canPrompt) {
      const outcome = await promptInstall();
      if (outcome !== 'accepted') setOpen(true); // offer manual steps if they cancel
      return;
    }
    setOpen(true); // iOS / desktop without a native prompt
  };

  return (
    <>
      <button onClick={handleClick} data-testid={testId} className={className}>
        <Download className="h-4 w-4" />
        {label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="install-app-dialog">
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
              {isIOS ? <Smartphone className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                     : <MonitorDown className="h-6 w-6 text-amber-600 dark:text-amber-400" />}
            </div>
            <DialogTitle className="text-center text-xl">Install Lumera</DialogTitle>
            <DialogDescription className="text-center">
              Add Lumera to your device for a full-screen, offline-ready Bible study app.
            </DialogDescription>
          </DialogHeader>

          {isIOS ? (
            <ol className="space-y-3 text-sm" data-testid="install-app-ios-steps">
              <li className="flex items-center gap-2">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-semibold">1</span>
                <span className="flex items-center gap-1">Tap the <Share2 className="inline h-4 w-4" /> <b>Share</b> button in Safari.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-semibold">2</span>
                <span className="flex items-center gap-1">Choose <Plus className="inline h-4 w-4" /> <b>Add to Home Screen</b>.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-semibold">3</span>
                <span className="flex items-center gap-1">Tap <Check className="inline h-4 w-4" /> <b>Add</b> — the Lumera icon appears on your Home Screen.</span>
              </li>
            </ol>
          ) : (
            <ol className="space-y-3 text-sm" data-testid="install-app-desktop-steps">
              <li className="flex items-center gap-2">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-semibold">1</span>
                <span>Open your browser menu (the <b>⋮</b> or <b>address-bar install</b> icon).</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-semibold">2</span>
                <span>Choose <b>Install Lumera</b> / <b>Add to Home Screen</b>.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-semibold">3</span>
                <span>Confirm — Lumera opens in its own window like a native app.</span>
              </li>
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallAppButton;
